import { useCallback, useMemo, useReducer } from 'react';
import type { MusicVideoDoc } from 'fym-shared/src/music24/musicVideoDoc';
import {
  initialMusicVideoDocState,
  musicVideoDocReducer,
  type MusicVideoDocAction,
  type MusicVideoDocState,
} from './musicVideoDocReducer';

/** Sends one action. A change with a `coalesce` key gets the time of now. */
export type MusicVideoDocDispatch = (action: MusicVideoDocAction) => void;

/** One change of the document: the change, and an optional key that joins a drag into one Undo step. */
export type ChangeDoc = (change: (doc: MusicVideoDoc) => MusicVideoDoc, coalesce?: string) => void;

export interface MusicVideoDocEditor {
  state: MusicVideoDocState;
  dispatch: MusicVideoDocDispatch;
  change: ChangeDoc;
  canUndo: boolean;
  canRedo: boolean;
}

/**
 * The document of the open cut with Undo and Redo (`musicVideoDocReducer`). The parent gives a new
 * `key` for another cut, so the history starts new with it.
 */
export function useMusicVideoDoc(initialDoc: MusicVideoDoc): MusicVideoDocEditor {
  const [state, rawDispatch] = useReducer(musicVideoDocReducer, initialDoc, initialMusicVideoDocState);
  const dispatch = useCallback<MusicVideoDocDispatch>((action) => {
    rawDispatch(action.type === 'change' && action.coalesce !== undefined ? { ...action, at: Date.now() } : action);
  }, []);
  const change = useCallback<ChangeDoc>(
    (fn, coalesce) => dispatch({ type: 'change', change: fn, coalesce }),
    [dispatch]
  );
  return useMemo(
    () => ({ state, dispatch, change, canUndo: state.past.length > 0, canRedo: state.future.length > 0 }),
    [change, dispatch, state]
  );
}
